import { useEffect } from 'react';
import { useI18n } from '../contexts/I18nContext';

const SEO_DATA = {
  de: {
    lang: 'de',
    title: 'leendr — Leihe alles. Von jedem. | Peer-to-Peer Mietplattform',
    description: 'leendr ist der Peer-to-Peer-Marktplatz, auf dem du Alltagsgegenstände leihen und verleihen kannst — von Werkzeugen bis Technik, Sportgeräten bis Partyzubehör. Sicher, einfach, nachhaltig.',
    ogTitle: 'leendr — Leihe alles. Von jedem.',
    ogDesc: 'Der Peer-to-Peer-Marktplatz zum Leihen und Verleihen von Alltagsgegenständen. Sicher, einfach, nachhaltig.',
    ogLocale: 'de_DE',
  },
  en: {
    lang: 'en',
    title: 'leendr — Borrow anything. From anyone. | Peer-to-Peer Rental Marketplace',
    description: 'leendr is the peer-to-peer rental marketplace where you can lend and borrow everyday items — from tools to tech, sports gear to party supplies. Safe, simple, sustainable.',
    ogTitle: 'leendr — Borrow anything. From anyone.',
    ogDesc: 'The peer-to-peer rental marketplace for lending and borrowing everyday items. Safe, simple, sustainable.',
    ogLocale: 'en_US',
  },
  fr: {
    lang: 'fr',
    title: 'leendr — Empruntez tout. À n\'importe qui. | Marketplace de location P2P',
    description: 'leendr est la marketplace peer-to-peer où vous pouvez prêter et emprunter des objets du quotidien — des outils à la tech, des équipements sportifs aux fournitures de fête. Sûr, simple, durable.',
    ogTitle: 'leendr — Empruntez tout. À n\'importe qui.',
    ogDesc: 'La marketplace peer-to-peer pour prêter et emprunter des objets du quotidien. Sûr, simple, durable.',
    ogLocale: 'fr_FR',
  },
  es: {
    lang: 'es',
    title: 'leendr — Pide prestado cualquier cosa. De cualquiera. | Marketplace de alquiler P2P',
    description: 'leendr es el marketplace peer-to-peer donde puedes prestar y pedir prestado objetos cotidianos — desde herramientas hasta tecnología, equipamiento deportivo hasta suministros para fiestas. Seguro, simple, sostenible.',
    ogTitle: 'leendr — Pide prestado cualquier cosa. De cualquiera.',
    ogDesc: 'El marketplace peer-to-peer para prestar y pedir prestado objetos cotidianos. Seguro, simple, sostenible.',
    ogLocale: 'es_ES',
  },
  uk: {
    lang: 'uk',
    title: 'leendr — Орендуй все. Від усіх. | P2P маркетплейс оренди',
    description: 'leendr — маркетплейс однорангової оренди повсякденних речей. Від інструментів до техніки, від спорту до вечірок. Безпечно, просто, екологічно.',
    ogTitle: 'leendr — Орендуй все. Від усіх.',
    ogDesc: 'Маркетплейс P2P-оренди повсякденних речей. Безпечно, просто, екологічно.',
    ogLocale: 'uk_UA',
  },
  ar: {
    lang: 'ar',
    title: 'leendr — استأجر أي شيء. من أي شخص. | سوق التأجير P2P',
    description: 'leendr هو سوق التأجير من شخص لشخص حيث يمكنك استئجار وتأجير الأغراض اليومية. آمن، بسيط، مستدام.',
    ogTitle: 'leendr — استأجر أي شيء. من أي شخص.',
    ogDesc: 'سوق التأجير من شخص لشخص للأغراض اليومية. آمن، بسيط، مستدام.',
    ogLocale: 'ar_SA',
  },
};

function setMeta(name, content, property = false) {
  const attr = property ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export default function SeoHead() {
  const { lang } = useI18n();

  useEffect(() => {
    const seo = SEO_DATA[lang] || SEO_DATA.de;

    // Update html lang
    document.documentElement.lang = seo.lang;

    // Title
    document.title = seo.title;

    // Standard meta
    setMeta('description', seo.description);

    // Open Graph
    setMeta('og:title', seo.ogTitle, true);
    setMeta('og:description', seo.ogDesc, true);
    setMeta('og:locale', seo.ogLocale, true);

    // Twitter
    setMeta('twitter:title', seo.ogTitle);
    setMeta('twitter:description', seo.ogDesc);

    // og:image stays the same for all languages
    setMeta('og:image', '/og-image.png', true);

    // JSON-LD Organization (Google Knowledge Panel / Rich Results)
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Leendr',
      legalName: 'Magic Concerts UG (haftungsbeschränkt)',
      url: 'https://leendr.de',
      logo: 'https://leendr.de/og-image.png',
      email: 'info@leendr.de',
      telephone: '+49-179-9700028',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Rheinpromenade 4a',
        postalCode: '40789',
        addressLocality: 'Monheim am Rhein',
        addressCountry: 'DE',
      },
      vatID: 'DE369303630',
    };
    let ld = document.getElementById('leendr-org-jsonld');
    if (!ld) {
      ld = document.createElement('script');
      ld.type = 'application/ld+json';
      ld.id = 'leendr-org-jsonld';
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify(jsonLd);
  }, [lang]);

  return null;
}
